<template>
  <div class="ml-4 mr-4">
    <form @submit.prevent="submit"
          @keydown="errors.clear()"
          autocomplete="off"
    >
      <div class="row py-3 align-items-center"
           v-if="type === 'create'"
      >
        <div class="col">
          <h1 class="company-h1">
            <button type="button"
                    class="btn btn-back"
                    @click="$router.push('/companies')"
            />
            Create Company
          </h1>
        </div>
        <div class="col" v-if="$_can('companies.update')">
          <button type="button"
                  class="btn btn-primary float-right ld-ext-right"
                  :class="loadingButton ? 'running' : ''"
                  :disabled="buttonDisabled"
                  @click="submit"
          >
            CREATE COMPANY
            <div class="ld ld-ball ld-flip" />
          </button>
        </div>
      </div>
      <div v-if="type !== 'create'">
        <div class="row py-3 align-items-center company-header">
          <div class="col-4">
            <h2 class="company-h1 mr-3">
              <button type="button"
                      class="btn btn-back"
                      @click="$router.push('/companies')"
              />
              {{ company.name }}
            </h2>
            <template v-if="company.status">
              <span v-if="company.status.name === 'Active'"
                    :class="'btn-status btn-active'"
              >
                {{ company.status.name }}</span>
              <span v-if="company.status.name === 'Inactive'"
                    :class="'btn-status btn-closed'"
              >
                {{ company.status.name }}</span>
              <div v-if="company.status.name === 'Setup Incomplete'"
                   :class="'btn-status btn-ncover info-company'"
              >
                {{ company.status.name }}
                <div class="btn btn-link p-0 tippy-popup tippy-info"
                     @mouseover="showTooltipStatus = true"
                     @mouseout="showTooltipStatus = false"
                >
                  <i class="fa fa-info-circle" />
                </div>
                <div class="popover tippy-info-popover"
                     v-if="showTooltipStatus"
                >
                  Setup incomplete is present when the company doesn't have one or more of the following: move sizes, arrival windows, merchant account, rates and coverage.
                </div>
              </div>
            </template>
          </div>
          <div class="col-8 d-flex align-items-center justify-content-end">
            <companies-visibility-bar
              :company="company"
              @saved="$emit('saved')"
              v-if="type !== 'setup' && $_can('companies.review.request')"
            />
            <button class="btn btn-primary ld-ext-right ml-5"
                    :class="loadingButton ? 'running' : ''"
                    :disabled="buttonDisabled"
                    v-if="$_can('companies.update')"
            >
              Save
              <div class="ld ld-ball ld-flip" />
            </button>
          </div>
        </div>
      </div>
      <div class="row l-m-b-16">
        <div class="col-6">
          <h2 class="l-header-2">Info</h2>
          <div class="row mb-3">
            <div class="col pr-1">
              <div class="select-wrap">
                <label>Name</label>
                <input v-model="company.name"
                       type="text"
                       autocomplete="no"
                       class="form-control"
                       placeholder="Enter Name"
                       :disabled="!$_can('companies.update')"
                />
              </div>
              <error-field :errors="errors" field="name" />
            </div>
            <div class="col pl-1">
              <div v-if="type === 'setup' && carriers && !carriers.length" class="select-wrap">
                <label>Default carrier</label>
                <button type="button"
                        class="btn add-button"
                        @click="openCreateCarrierModal"
                >
                  <span>
                    <span class="mod-icon-add" />
                  </span>Create new
                </button>
              </div>
              <div v-else class="select-wrap">
                <label>Default carrier</label>
                <simple-select v-model="company.default_carrier_id"
                               :options="carriers"
                               placeholder="Select Carrier"
                               :disabled="!$_can('companies.update')"
                />
              </div>
              <error-field :errors="errors" field="default_carrier_id" />
            </div>
          </div>
          <div class="row mb-3">
            <div class="col pr-1">
              <div class="select-wrap">
                <label>Email</label>
                <input v-model="company.email"
                       type="text"
                       autocomplete="no"
                       class="form-control"
                       placeholder="Enter Email"
                       :disabled="!$_can('companies.update')"
                />
              </div>
              <error-field :errors="errors" field="email" />
            </div>
            <div class="col pl-1">
              <div class="select-wrap">
                <label>Phone</label>
                <input v-model="company.phone"
                       type="text"
                       autocomplete="no"
                       class="form-control"
                       @keydown="maskPhone($event)"
                       placeholder="Enter Phone"
                       maxlength="14"
                       :disabled="!$_can('companies.update')"
                />
              </div>
              <error-field :errors="errors" field="phone" />
            </div>
          </div>
          <div class="row">
            <div class="col">
              <hr class="" />
            </div>
          </div>
        </div>
        <div class="col-6">
          <h2 class="l-header-2">Pricing</h2>
          <div class="row mb-3">
            <div class="col pr-1">
              <div class="select-wrap">
                <label>Min hours</label>
                <flat-pickr v-model="company.minimum_hours"
                            :config="timeConfig"
                            class="form-control"
                            placeholder="Select"
                            :disabled="!$_can('companies.update')"
                            red="flatPickr"
                            data-input
                />
              </div>
              <error-field :errors="errors" field="minimum_hours" />
            </div>
            <div class="col pl-1">
              <div class="select-wrap">
                <label>Custom rate</label>
                <simple-select v-model="company.custom_rate"
                               :options="booleanOptions"
                               placeholder="Custom Rate"
                               :disabled="!$_can('companies.update')"
                />
              </div>
              <error-field :errors="errors" field="custom_rate" />
            </div>
            <div class="col pl-1">
              <div class="select-wrap">
                <label>Deposit</label>
                <input v-model="company.deposit_value"
                       type="text"
                       autocomplete="no"
                       class="form-control"
                       placeholder="Enter Deposit"
                       :disabled="!$_can('companies.update')"
                />
              </div>
              <error-field :errors="errors" field="deposit_value" />
            </div>
          </div>
          <div class="row mb-3">
            <div class="col pr-1">
              <div class="select-wrap">
                <label>Unit</label>
                <div class="simple-select">
                  <div class="simple-select-header simple-select-header"
                       @click="$_can('companies.update') ? dropdownUnit = !dropdownUnit : null"
                  >
                    <input :placeholder="unitOptions.find(el => el.id === company.deposit_unit).name"
                           v-if="company.deposit_unit"
                           class="simple-select-input"
                           readonly
                           @blur="closeList()"
                           :disabled="!$_can('companies.update')"
                    />
                    <span class="simple-select-caret"><i class="fa fa-caret-down" /></span>
                  </div>
                  <div class="simple-select-options"
                       v-if="dropdownUnit"
                  >
                    <ul>
                      <li class="select-option select-option-boolean"
                          @click="setDepositUnit('$')"
                          style="pointer-events: auto;"
                      >
                        <span class="select-label">$</span>
                      </li>
                      <li class="select-option select-option-boolean"
                          @click="setDepositUnit('%')"
                          style="pointer-events: auto;"
                      >
                        <span class="select-label">%</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <error-field :errors="errors" field="deposit_unit" />
            </div>
            <div class="col pl-1">
              <div class="select-wrap">
                <label>Truck price</label>
                <input v-model="company.truck_price"
                       type="text"
                       autocomplete="no"
                       class="form-control"
                       placeholder="Enter Price"
                       :disabled="!$_can('companies.update')"
                />
              </div>
              <error-field :errors="errors" field="truck_price" />
            </div>
            <div class="col pl-1">
              <div class="select-wrap">
                <label>Drive back addition</label>
                <simple-select
                  v-model="company.drive_back_addition"
                  :options="driveBackAdditionOptions"
                  label="label"
                />
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <hr />
            </div>
          </div>
        </div>
      </div>
      <div class="row l-m-b-16" v-if="type !== 'edit'">
        <div class="col-6">
          <h2 class="l-header-2">Order number prefix</h2>
          <div class="row">
            <div class="col">
              <div class="select-wrap">
                <label>Prefix</label>
                <input v-model="company.ref_id_prefix"
                       type="text"
                       autocomplete="no"
                       class="form-control"
                       maxlength="5"
                       placeholder="Enter Prefix"
                />
                <div class="btn p-0 tippy-popup tippy-info" @mouseover="showTooltipPrefix = true"
                     @mouseout="showTooltipPrefix = false"
                >
                  <i class="fa fa-info-circle" />
                  <div class="popover tippy-info-right" v-if="showTooltipPrefix">
                    Prefix will be generated automatically if you don't add it, note that once generated you won't be able
                    to change it
                  </div>
                </div>
              </div>
              <error-field :errors="errors" field="ref_id_prefix" />
            </div>
          </div>
        </div>
        <div class="col-6">
          <h2 class="l-header-2">Rate Settings</h2>
          <div class="row mb-3">
            <div class="col pr-1">
              <div class="select-wrap">
                <label>Fixed rate</label>
                <simple-select v-model="company.fixed_rate"
                               :options="booleanOptions"
                               placeholder="Select"
                />
              </div>
              <error-field :errors="errors" field="fixed_rate" />
            </div>
            <div class="col pl-1">
              <div class="select-wrap">
                <label>Offer duration</label>
                <input v-model="company.offer_duration"
                       type="text"
                       autocomplete="no"
                       class="form-control"
                       placeholder="Enter Duration"
                />
              </div>
              <error-field :errors="errors" field="offer_duration" />
            </div>
            <div class="col pl-1">
              <div class="select-wrap">
                <label>Pre-agreement sending</label>
                <simple-select v-model="company.customer_contract_automatic"
                               :options="booleanOptions"
                               placeholder="Pre-agreement sending"
                />
              </div>
              <error-field :errors="errors" field="customer_contract_automatic" />
            </div>
          </div>
          <div class="row">
            <div class="col-4 pr-1">
              <div class="select-wrap">
                <label>Cash discount value</label>
                <input
                  v-model="company.cash_discount_value"
                  type="text"
                  autocomplete="no"
                  class="form-control"
                  placeholder="Cash discount"
                  :disabled="!$_can('companies.update')"
                />
              </div>
              <error-field :errors="errors" field="cash_discount_value" />
            </div>
            <div class="col-4 pl-1">
              <div class="select-wrap">
                <label>Unit</label>
                <div class="simple-select">
                  <div class="simple-select-header simple-select-header"
                       @click="$_can('companies.update') ? dropdownUnitDiscount = !dropdownUnitDiscount : null"
                  >
                    <input :placeholder="company.cash_discount_unit"
                           class="simple-select-input"
                           readonly
                           @blur="closeList()"
                           :disabled="!$_can('companies.update')"
                    />
                    <span class="simple-select-caret"><i class="fa fa-caret-down" /></span>
                  </div>
                  <div class="simple-select-options"
                       v-if="dropdownUnitDiscount"
                  >
                    <ul>
                      <li class="select-option select-option-boolean"
                          @click="setDepositUnitCash('$')"
                      >
                        <span class="select-label">$</span>
                      </li>
                      <li class="select-option select-option-boolean"
                          @click="setDepositUnitCash('%')"
                      >
                        <span class="select-label">%</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <error-field :errors="errors" field="cash_discount_unit" />
            </div>
            <div class="col-4 pl-1">
              <div class="select-wrap">
                <label>Double drive time</label>
                <simple-select v-model="company.double_drive"
                               :options="booleanOptions"
                               placeholder="Select"
                />
              </div>
              <error-field :errors="errors" field="double_drive" />
            </div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-6">
          <h2 class="l-header-2">Address</h2>
          <div class="row mb-3">
            <div class="col pr-1">
              <div class="select-wrap">
                <label>State</label>
                <simple-select v-model="company.state_id"
                               :options="states"
                               placeholder="Select State"
                               autocomplete="no"
                               :disabled="!$_can('companies.update')"
                />
              </div>
              <error-field :errors="errors" field="state_id" />
            </div>
            <div class="col px-1">
              <div class="select-wrap">
                <label>City</label>
                <input v-model="company.city"
                       type="text"
                       autocomplete="no"
                       class="form-control"
                       placeholder="Enter City Name"
                       :disabled="!$_can('companies.update')"
                />
              </div>
              <error-field :errors="errors" field="city" />
            </div>
            <div class="col pl-1">
              <div class="select-wrap">
                <label>Zip</label>
                <input v-model="company.zip"
                       type="text"
                       autocomplete="no"
                       class="form-control"
                       placeholder="Enter ZIP"
                       @input="getLocationFromZip(company.zip)"
                       :disabled="!$_can('companies.update')"
                />
              </div>
              <error-field :errors="errors" field="zip" />
            </div>
          </div>
          <div class="row">
            <div class="col pr-1">
              <div class="select-wrap">
                <label>Address</label>
                <input v-model="company.address"
                       type="text"
                       autocomplete="no"
                       class="form-control"
                       placeholder="Enter Address"
                       :disabled="!$_can('companies.update')"
                />
              </div>
              <error-field :errors="errors" field="address" />
            </div>
            <div class="col pl-1">
              <div class="select-wrap">
                <label>Address 2</label>
                <input v-model="company.address2"
                       type="text"
                       autocomplete="no"
                       class="form-control"
                       placeholder="Apartment, Suite, Unit"
                       :disabled="!$_can('companies.update')"
                />
              </div>
              <error-field :errors="errors" field="address2" />
            </div>
          </div>
          <div class="row">
            <div class="col">
              <hr class="" />
            </div>
          </div>
          <div class="row mb-4" style="position: relative">
            <div class="col-12">
              <h2 class="l-header-2">
                Time settings
              </h2>
              <div class="row">
                <div class="col-6 pr-1">
                  <div class="select-wrap">
                    <label>Timezone</label>
                    <simple-select
                      v-model="company.timezone"
                      :options="timezones"
                      placeholder="Select time zone"
                    />
                  </div>
                  <span class="timezone-tooltip" v-if="showTooltipTimezone">
                    We set your timezone to match your location, if you'd like to change it, use this field.
                  </span>
                </div>
                <div class="col-6 pl-1">
                  <div class="select-wrap">
                    <label>Time increment</label>
                    <simple-select v-model="company.minute_increment"
                                   :options="timeIncrementOptions"
                                   label="label"
                    />
                  </div>
                </div>
                <div
                  class="info-tooltip"
                  @mouseover="tooltip = true"
                  @mouseleave="tooltip = false"
                >
                  <i class="fa fa-info-circle pr-2" />
                  <div class="popover tippy-info-up" v-if="tooltip">
                    Selected time increment will round up your total job time and allow you to select minute increments on the job.
                    If you choose option for 10 minutes and your job took 04:53 it will round up to 04:50.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-6">
          <h2 class="l-header-2">Parking Address</h2>
          <div class="row mb-3">
            <div class="col pr-3 pr-md-1 pr-lg-1 pr-xl-1 mb-2 mb-lg-0 mb-md-0 mb-xl-0">
              <div class="select-wrap">
                <label>Search parking address</label>
                <input v-model="company.parking_full_address"
                       type="text"
                       class="form-control"
                       @input="getAddressData(company.parking_full_address)"
                       @keydown.prevent.down="$event.target.nextElementSibling && $event.target.nextElementSibling.firstChild.focus()"
                />
                <div class="dropdown-menu show address-field-wrap"
                     aria-labelledby="dropdownMenu2"
                     v-if="showDropdown === true && addresses"
                >
                  <button type="button"
                          class="dropdown-item"
                          v-for="(addressItem, indexAddr) in addresses"
                          :key="`btn-prefix-${indexAddr}`"
                          @click="setAddress(addressItem)"
                          @keydown.enter="setAddress(addressItem)"
                          @keydown.prevent.down="$event.target.nextElementSibling.focus()"
                          @keydown.up="$event.target.previousElementSibling.focus()"
                  >
                    {{ addressItem.full_address }}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="row mb-3">
            <div class="col pr-1">
              <div class="select-wrap">
                <label>State</label>
                <simple-select v-model="company.parking_state_id"
                               :options="states"
                               placeholder="Select State"
                               autocomplete="no"
                               :disabled="!$_can('companies.update')"
                />
              </div>
              <error-field :errors="errors" field="parking_state_id" />
            </div>
            <div class="col px-1">
              <div class="select-wrap">
                <label>City</label>
                <input v-model="company.parking_city"
                       type="text"
                       autocomplete="no"
                       class="form-control"
                       placeholder="Enter City Name"
                       :disabled="!$_can('companies.update')"
                />
              </div>
              <error-field :errors="errors" field="parking_city" />
            </div>
            <div class="col pl-1">
              <div class="select-wrap">
                <label>Zip</label>
                <input v-model="company.parking_zip"
                       type="text"
                       autocomplete="no"
                       class="form-control"
                       placeholder="Enter ZIP"
                       :disabled="!$_can('companies.update')"
                />
              </div>
              <error-field :errors="errors" field="parking_zip" />
            </div>
          </div>
          <div class="row">
            <div class="col pr-1">
              <div class="select-wrap">
                <label>Address</label>
                <input v-model="company.parking_address"
                       type="text"
                       autocomplete="no"
                       class="form-control"
                       placeholder="Enter Address"
                       :disabled="!$_can('companies.update')"
                />
              </div>
              <error-field :errors="errors" field="parking_address" />
            </div>
            <div class="col pl-1">
              <div class="select-wrap">
                <label>Address 2</label>
                <input v-model="company.parking_address2"
                       type="text"
                       autocomplete="no"
                       class="form-control"
                       placeholder="Apartment, Suite, Unit"
                       :disabled="!$_can('companies.update')"
                />
              </div>
              <error-field :errors="errors" field="parking_address2" />
            </div>
          </div>
          <div class="row">
            <div class="col">
              <hr />
            </div>
          </div>
        </div>
        <div v-if="type === 'edit'"
             class="col-6"
        >
          <h2 class="l-header-2">Rate Settings</h2>
          <div class="row mb-3">
            <div class="col pr-1">
              <div class="select-wrap">
                <label>Fixed rate</label>
                <div class="simple-select">
                  <div class="simple-select-header simple-select-header"
                       @click="$_can('companies.update') ? dropdown = true : null"
                  >
                    <input :placeholder="booleanOptions.find(el => el.id === company.fixed_rate).name"
                           v-if="company.fixed_rate"
                           class="simple-select-input"
                           @blur="closeList()"
                           :disabled="!$_can('companies.update')"
                    />
                    <span class="simple-select-caret"><i class="fa fa-caret-down" /></span>
                  </div>
                  <div class="simple-select-options"
                       v-if="dropdown"
                  >
                    <ul>
                      <li class="select-option select-option-boolean"
                          @click="setRate(1)"
                          style="pointer-events: auto;"
                      >
                        <span class="select-label">Yes</span>
                      </li>
                      <li class="select-option select-option-boolean"
                          @click="setRate(0)"
                          style="pointer-events: auto;"
                      >
                        <span class="select-label">No</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <error-field :errors="errors" field="fixed_rate" />
            </div>
            <div class="col pl-1">
              <div class="select-wrap">
                <label>Offer duration</label>
                <input v-model="company.offer_duration"
                       type="text"
                       autocomplete="no"
                       class="form-control"
                       placeholder="Enter Duration"
                />
              </div>
              <error-field :errors="errors" field="offer_duration" />
            </div>
            <div class="col pl-1">
              <div class="select-wrap">
                <label>Pre-agreement sending</label>
                <simple-select v-model="company.customer_contract_automatic"
                               :options="booleanOptions"
                               placeholder="Pre-agreement sending"
                />
              </div>
              <error-field :errors="errors" field="customer_contract_automatic" />
            </div>
          </div>
          <div class="row">
            <div class="col-4 pr-1">
              <div class="select-wrap">
                <label>Cash discount value</label>
                <input
                  v-model="company.cash_discount_value"
                  type="text"
                  autocomplete="no"
                  class="form-control"
                  placeholder="Cash discount"
                  :disabled="!$_can('companies.update')"
                />
              </div>
              <error-field :errors="errors" field="cash_discount_value" />
            </div>
            <div class="col-4 pl-1">
              <div class="select-wrap">
                <label>Unit</label>
                <div class="simple-select">
                  <div class="simple-select-header simple-select-header"
                       @click="$_can('companies.update') ? dropdownUnitDiscount = !dropdownUnitDiscount : null"
                  >
                    <input :placeholder="company.cash_discount_unit"
                           class="simple-select-input"
                           @blur="closeList()"
                           readonly
                           :disabled="!$_can('companies.update')"
                    />
                    <span class="simple-select-caret"><i class="fa fa-caret-down" /></span>
                  </div>
                  <div class="simple-select-options"
                       v-if="dropdownUnitDiscount"
                  >
                    <ul>
                      <li class="select-option select-option-boolean"
                          @click="setDepositUnitCash('$')"
                      >
                        <span class="select-label">$</span>
                      </li>
                      <li class="select-option select-option-boolean"
                          @click="setDepositUnitCash('%')"
                      >
                        <span class="select-label">%</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <error-field :errors="errors" field="cash_discount_unit" />
            </div>
            <div class="col-4 pl-1">
              <div class="select-wrap">
                <label>Double drive time</label>
                <simple-select v-model="company.double_drive"
                               :options="booleanOptions"
                               placeholder="Select"
                />
              </div>
              <error-field :errors="errors" field="double_drive" />
            </div>
          </div>
          <div class="row">
            <div class="col">
              <hr class="" />
            </div>
          </div>
          <h2 class="l-header-2">Cluster fee</h2>
          <div class="row mb-3">
            <div class="col pr-1">
              <div class="select-wrap amount-wrap-small">
                <label>Cluster fee</label>
                <span class="dollar mt-3 pt-1">$</span>
                <input v-model="company.cluster_fee"
                       type="text"
                       autocomplete="no"
                       class="form-control"
                       placeholder="Enter Cluster fee"
                />
              </div>
              <error-field :errors="errors" field="cluster_fee" />
            </div>
            <div class="col pl-1">
              <div class="select-wrap amount-wrap-small">
                <label>Cluster percent</label>
                <span class="dollar mt-3 pt-1">%</span>

                <input v-model="company.cluster_percent"
                       type="text"
                       autocomplete="no"
                       class="form-control pl-4"
                       placeholder="Enter Cluster percent"
                />
              </div>
              <error-field :errors="errors" field="cluster_percent" />
            </div>
          </div>
        </div>
      </div>
      <div class="row" />
      <div class="row mb-4" v-if="type === 'edit'">
        <div class="col d-flex justify-content-end">
          <button type="button" class="btn btn-primary"
                  v-if="$_can('companies.update') && company.status_id === 2"
                  @click="setCompanyStatus(1)"
                  :disabled="!$_can('companies.update')"
          >
            Activate
          </button>
          <button type="button" class="btn btn-delete"
                  v-if="$_can('companies.update') && company.status_id === 1"
                  @click="setCompanyStatus(2)"
          >
            Deactivate
          </button>
          <error-field :errors="errors" field="status_id" />
        </div>
      </div>
    </form>

    <!-- Create carrier -->
    <modal v-if="showCreateCarrierModal" @close="showCreateCarrierModal = false" size="modal-medium">
      <div slot="header">
        <h4>Create Carrier</h4>
      </div>
      <div slot="body" style="">
        <template>
          <div class="row">
            <div class="col-12">
              <div class="select-wrap mb-2">
                <label class="m-label">Name</label>
                <input type="text" class="form-control"
                       v-model="carrier.name"
                       @input="errors.clear('carrierName')"
                />
              </div>
              <error-field :errors="errors" field="carrierName" />
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <button type="button" class="btn btn-primary ld-ext-right pull-right"
                      :class="carrierLoadingButton ? 'running' : ''"
                      :disabled="carrierFormInvalid"
                      @click="createCarrier"
              >
                Create
                <div class="ld ld-ball ld-flip" />
              </button>
            </div>
          </div>
        </template>
      </div>
      <div slot="footer" />
    </modal>
  </div>
</template>

<script>
import axios from 'axios';
import toastr from 'toastr';
import _ from 'lodash';
import { mapActions, mapMutations, mapState } from 'vuex';
import flatPickr from 'vue-flatpickr-component';
import Errors from '../../classes/Errors';
import ErrorField from '../ErrorField';
import Modal from '../Modal';
import PhoneMask from '../../classes/PhoneMask';
import CompaniesVisibilityBar from './CompaniesVisibilityBar';
import 'flatpickr/dist/flatpickr.css';

export default {
  components: {
    CompaniesVisibilityBar,
    ErrorField,
    flatPickr,
    Modal,
  },
  props: {
    type: {
      type: String,
      default: 'edit',
    },
    company: {
      type: Object,
      default: () => ({
        address2: '',
        address: '',
        cash_discount_unit: '',
        cash_discount_value: '',
        city: '',
        custom_rate: null,
        default_carrier_id: '',
        deposit_unit: '$',
        deposit_value: '',
        email: '',
        fixed_rate: 1,
        meta_description: '',
        meta_title: '',
        minimum_hours: '',
        name: '',
        offer_duration: 2,
        parking_address: '',
        parking_address2: '',
        parking_full_address: '',
        parking_city: '',
        parking_latitude: '',
        parking_longitude: '',
        parking_state: '',
        parking_state_id: '',
        parking_zip: '',
        phone: '',
        ref_id_prefix: '',
        state_id: '',
        truck_price: '',
        zip: '',
      }),
    },
  },
  data: () => ({
    addresses: [],
    booleanOptions: [{ id: 1, name: 'Yes' }, { id: 0, name: 'No' }],
    buttonDisabled: false,
    carrier: {
      name: '',
    },
    carrierLoadingButton: false,
    driveBackAdditionOptions: [
      {
        id: 0,
        label: 'No',
      },
      {
        id: 1,
        label: 'Yes',
      },
    ],
    dropdown: false,
    dropdownUnit: false,
    dropdownUnitDiscount: false,
    errors: new Errors(),
    loadingButton: false,
    phoneMask: new PhoneMask(),
    showCreateCarrierModal: false,
    showDropdown: false,
    showTooltipPrefix: false,
    showTooltipStatus: false,
    showTooltipTimezone: false,
    step: 1,
    timeConfig: {
      enableTime: true,
      noCalendar: true,
      dateFormat: 'H:i',
      time_24hr: true,
      defaultDate: '00:00',
    },
    timeIncrementOptions: [
      {
        id: 1,
        label: '1 minute',
      },
      {
        id: 5,
        label: '5 minutes',
      },
      {
        id: 10,
        label: '10 minutes',
      },
      {
        id: 15,
        label: '15 minutes',
      },
    ],
    tooltip: false,
    unitOptions: [{ id: '$', name: '$' }, { id: '%', name: '%' }],
  }),
  computed: {
    ...mapState({
      carriers: state => state.carriers.carriers.data,
      companyStatuses: state => state.values.companyStatuses,
      states: state => state.values.states,
      timezones: state => state.timezones.timezones.data,
      user: state => state.auth.dispatchUser,
    }),
    carrierFormInvalid() {
      return !this.carrier.name.length;
    },
  },
  mounted() {
    this.getStates();
    this.getTimezones();
    if (this.type === 'create') {
      this.carrier.name = this.company.name;
      this.company.custom_rate = 0;
    }
  },
  methods: {
    ...mapActions(['getCarriers', 'getCompanies', 'getStates', 'getTimezones']),
    ...mapMutations(['setCompanyStep']),
    submit() {
      if (this.type === 'create') {
        this.createCompany();
      } else {
        this.editCompany();
      }
    },
    createCarrier() {
      this.buttonDisabled = true;
      this.carrierLoadingButton = true;
      axios
        .post('carriers', { ...this.carrier })
        .then(res => {
          toastr.success('Successfully created carrier');
          this.company.default_carrier_id = res.data.data.id;
          this.showCreateCarrierModal = false;
          this.getCarriers();
        })
        .catch(err => {
          if (err.response.data.error.name) {
            this.errors.record({ carrierName: [err.response.data.error.name] });
          }
        })
        .finally(() => {
          this.buttonDisabled = false;
          this.carrierLoadingButton = false;
        });
    },
    createCompany() {
      this.buttonDisabled = true;
      this.loadingButton = true;
      axios
        .post(`companies`, this.company)
        .then(() => {
          toastr.success('Successfully created company');
          this.$emit('saved');
          this.setCompanyStep('companies-created');
          this.getCompanies();
        })
        .catch(err => {
          this.errors.record(err.response.data.error);
        })
        .finally(() => {
          this.loadingButton = false;
          this.buttonDisabled = false;
        });
    },
    editCompany() {
      const id = this.type === 'setup' ? this.user.companies[0].id : this.$route.params.id;
      const method = this.type === 'setup' ? 'put' : 'patch';
      this.buttonDisabled = true;
      this.loadingButton = true;
      delete this.company.send_automatic_emails;
      axios[method](`companies/${id}`, this.company)
        .then(res => {
          toastr.success('Successfully saved company info');
          this.$emit('saved');
          res.data.data.zip = res.data.data.zip.zip;
          this.errors.clear();
          this.getCompanies();
        })
        .then(() => {
          this.$set(
            this.company,
            'phone',
            this.company.phone ? this.phoneMask.maskedPhone(this.company.phone) : '',
          );
          if (this.type === 'setup') {
            this.$store.dispatch('authRefresh').then(() => {
              this.$router.push({ name: 'CompaniesEdit', params: { id } });
            });
          }
        })
        .catch(err => {
          this.errors.record(err.response.data.error);
        })
        .finally(() => {
          this.buttonDisabled = false;
          this.loadingButton = false;
        });
    },
    getAddressData: _.debounce(function call(streetName) {
      if (streetName.length < 3) {
        this.showDropdown = false;
      } else {
        if (typeof this.source !== typeof undefined) {
          this.source.cancel('Operation canceled due to new request.');
        }
        this.source = axios.CancelToken.source();
        this.showDropdown = true;
        axios
          .get(`autocomplete?address=${encodeURIComponent(streetName)}`, {
            cancelToken: this.source.token,
          })
          .then(res => {
            this.addresses = res.data.data;
          })
          .catch(err => {
            if (!axios.isCancel(err)) {
              this.errors.record(err.response.data.error);
            }
          });
      }
    }, 500),
    getLocationFromZip: _.debounce(function call(zip) {
      if (zip.length > 4 && this.type === 'create') {
        this.showTooltipTimezone = true;
        axios.get(`/zips/${zip}`).then(res => {
          this.$set(this.company, 'timezone', res.data.data.timezone);
        });
        setTimeout(() => {
          this.showTooltipTimezone = false;
        }, 10000);
      }
    }, 800),
    openCreateCarrierModal() {
      this.showCreateCarrierModal = true;
    },
    setAddress(address) {
      axios
        .get(
          `geocode?place_id=${encodeURIComponent(address.place_id)}&address=${encodeURIComponent(
            address.full_address,
          )}`,
        )
        .then(res => {
          [address] = res.data.data;
          this.$set(this.company, 'parking_full_address', address.full_address);
          this.$set(
            this.company,
            'parking_address',
            `${address.street_number} ${address.street_name}`,
          );
          this.$set(this.company, 'parking_city', address.city);
          this.$set(this.company, 'parking_latitude', address.latitude);
          this.$set(this.company, 'parking_longitude', address.longitude);
          this.$set(this.company, 'parking_state', address.state);
          this.$set(this.company, 'parking_state_id', address.state_id);
          this.$set(this.company, 'parking_zip', address.postal_code);
          console.log(address);
        })
        .catch(err => {
          this.errors.record(err.response.data.error);
        });
      this.showDropdown = false;
    },
    setCompanyStatus(statusId) {
      this.buttonDisabled = true;
      this.loadingButton = true;
      const id = this.type === 'setup' ? this.user.companies[0].id : this.$route.params.id;
      axios
        .put(`companies/${id}`, { status_id: statusId })
        .then(() => {
          toastr.success('Successfully saved company status');
          this.errors.clear();
          this.getCompanies();
          this.$emit('saved');
        })
        .catch(err => {
          this.errors.record(err.response.data.error);
        })
        .finally(() => {
          this.loadingButton = false;
          this.buttonDisabled = false;
        });
    },
    setRate(fixedRate) {
      this.company.fixed_rate = fixedRate;
      this.closeList();
    },
    setDepositUnit(unit) {
      this.company.deposit_unit = unit;
      this.closeList();
    },
    setDepositUnitCash(unit) {
      this.company.cash_discount_unit = unit;
      this.closeList();
    },
    closeList() {
      const vm = this;
      setTimeout(() => {
        vm.dropdown = false;
        vm.dropdownUnit = false;
        vm.dropdownUnitDiscount = false;
      }, 100);
    },
    maskPhone() {
      this.$nextTick(() => {
        this.$set(this.company, 'phone', this.phoneMask.maskedPhone(this.company.phone));
      });
    },
    makeVisible() {
      axios
        .post(`companies/${this.company.id}/request-review`)
        .then(res => {
          this.company = res.data.data;
          toastr.success('Request sent for review to mod24');
        })
        .catch(err => this.errors.record(err.response.data.error));
    },
    cancelVisibility() {
      axios
        .post(`companies/${this.company.id}/request-review`)
        .then(res => {
          this.company = res.data.data;
          toastr.success('Request sent for cancelling visibility');
        })
        .catch(err => this.errors.record(err.response.data.error));
    },
  },
};
</script>
<style lang="scss" scoped>
.add-button {
  padding: 0 0 2px 16px;
}
.info-tooltip {
  position: absolute;
  top: 44px;
  right: -2%;
  z-index: 49;
}
.timezone-tooltip {
  display: flex;
  font-size: 12px;
  color: white;
  background-color: #131c36;
  border-radius: 3px;
  padding: 14px;
  margin-top: 10px;
}
.timezone-tooltip::after {
  content: ' ';
  position: absolute;
  bottom: 53%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: transparent transparent #131c36 transparent;
}
</style>



// WEBPACK FOOTER //
// src/components/companies/CompaniesInfo.vue